import { FC, ReactNode, useEffect, useState } from "react";
import { IConfiguracaoFinanceiraDom, IPropostaDadosRequestDom, TTipoProposta } from "./types";
import { Clientes } from "src/types";
import "./styles.scss";
import { TabContent, TabControl } from "src/framework/components/navigations";
import theme from "src/theme/light";
import { Avatar } from "@material-ui/core";
import { FaBriefcase, FaExclamationCircle, FaRing, FaUserAlt, FaUsers } from "react-icons/fa";
import { AiFillBank } from "react-icons/ai";
import { ESTADO_CIVIL_CASADO_ID } from "./constants";
import { FormPessoa } from "./FormPessoa";
import { FormProfissao } from "./FormProfissao";
import { FormDadosBancarios } from "./FormDadosBancarios";
import { FormAvalistas } from "./FormAvalistas";
import { CollectionUtil } from "src/framework/commons";
import { ArrayUtil } from "src/framework/commons/ArrayUtil";
import { Alert } from "@material-ui/lab";
import { useObservable } from "src/framework/store/storeUtil";
import { profissoesList$, naturezaLegalList$, relacionamentosList$, tipoProposta$, empresaAtividadeList$, gruposAtividadeList$, empresaTipoList$, errorsList$ } from "../negociacao/Financiamento";

const TAB_DADOS_PESSOAIS = 'dados_pessoais'
const TAB_DADOS_CONJUGE = 'dados_conjuge'
const TAB_DADOS_BANCARIOS = 'dados_bancarios'
const TAB_DADOS_PROFISSIONAIS = 'dados_profissionais'
const TAB_DADOS_AVALISTAS = 'dados_avalistas'
interface TabError {
    [tab: string]: boolean;
}

const TAB_ERROR: TabError = {
    "dados_pessoais": false,
    "dados_pessoa_juridica": false,
    "dados_pessoais.conjuge": false,
    "dados_pessoais.profissionais": false,
    "dados_bancarios": false,
    "avalistas": false,
}

interface FinanciamentoProperties {
    dados: IPropostaDadosRequestDom,
    dadosCliente?: Clientes | null;
    onChange: (newPropostaDados: IPropostaDadosRequestDom) => void,
    isValid?: (value: boolean) => void;
    readonly?: boolean;
    configfuracoesFinanceira: IConfiguracaoFinanceiraDom | null;
}

interface FinanceiroConfiguracao {
    permite_conjuge: boolean;
    permite_avalistas: boolean;
}

const Financiamento: FC<FinanciamentoProperties> = ({
    dados,
    dadosCliente,
    onChange,
    isValid,
    configfuracoesFinanceira,
    readonly = false
}) => {

    const [activeTab, setActiveTab] = useState<string>("dados_pessoais")
    const [tabError, setTabError] = useState<TabError>(TAB_ERROR);

    const tipoProposta = useObservable(tipoProposta$)
    const errorsList = useObservable<string[] | undefined>(errorsList$) || []
    
    const handleChangeTabManutencao = (tab: string) => {
        setActiveTab(tab);
    }

    const isPessoaFisica = (): boolean => {
        return tipoProposta === "fisica"
    }

    const resolveConfiguracoesFinanceiro = (): FinanceiroConfiguracao => {
        const _financeiraConfig: FinanceiroConfiguracao = {
            permite_conjuge: false,
            permite_avalistas: false,
        }
        if (isPessoaFisica() && configfuracoesFinanceira?.pessoa_fisica === true) {
            _financeiraConfig.permite_conjuge = configfuracoesFinanceira?.permite_conjuge ? configfuracoesFinanceira.permite_conjuge : false;
            _financeiraConfig.permite_avalistas = configfuracoesFinanceira?.permite_avalistas || false;
        }

        if (!isPessoaFisica()) {
            _financeiraConfig.permite_avalistas = true;
        }

        return _financeiraConfig;
    }

    const resolveDados = (): any => {
        let _state = dados.pessoa_fisica;

        if (!isPessoaFisica()) {
            _state = dados.pessoa_juridica;
        }

        return _state
    }

    const renderAlert = (): ReactNode => {
        return <>
            <div className="X12 alert-container" style={{marginTop: "10px"}}>
                <Alert 
                    severity="warning"
                >
                    Você poderá <strong>salvar</strong> o formulário sem precisar completar o cadastro.
                    <br/>Porém, os campos destacados abaixo são necessários para o <strong>envio</strong> da proposta.
                </Alert>
            </div>
        </>
    } 

    useEffect(() => {
        setTabError(CollectionUtil.merge<TabError>({}, TAB_ERROR))
    }, []);

    useEffect(() => {
        const _tabErrorState = CollectionUtil.merge<TabError>({}, tabError);
        _tabErrorState["dados_pessoais"] = ArrayUtil.contains(errorsList, "pessoa_fisica#")
        _tabErrorState["dados_pessoa_juridica"] = ArrayUtil.contains(errorsList, "pessoa_juridica#")
        _tabErrorState["dados_pessoais.conjuge"] = ArrayUtil.contains(errorsList, "pessoa_fisica.conjuge#")
        _tabErrorState["dados_pessoais.profissionais"] = ArrayUtil.contains(errorsList, "pessoa_fisica.ocupacao#")
        _tabErrorState["dados_bancarios"] = ArrayUtil.contains(errorsList, "dados_bancarios#")
        _tabErrorState["avalistas"] = ArrayUtil.contains(errorsList, "avalistas#")
        setTabError(_tabErrorState)
    }, [errorsList])

    return <div className="Financiamento">

        <TabControl
            currentTabName={activeTab}
            onChangeTab={handleChangeTabManutencao}
            indicatorColor="primary"
            textColor="primary"
            fixHeader
        >
            <TabContent name={TAB_DADOS_PESSOAIS} 
                visible={true}
                label={<>
                    <Avatar variant="rounded" color={theme.palette.primary.main}>
                        <FaUserAlt />
                    </Avatar> {isPessoaFisica() ? "Dados Pessoais" : "Pessoa Jurídica"}{(isPessoaFisica() && tabError["dados_pessoais"] || (!isPessoaFisica() && tabError["dados_pessoa_juridica"])) ? <div className="error-alert"><FaExclamationCircle color={theme.palette.error.main} /></div> : ""}
                </>} 
            >
                <div className="row">
                    {ArrayUtil.contains(errorsList,"pessoa_fisica#") && <>{renderAlert()}</>}
                    
                    <div className="X12">
                        <FormPessoa
                            readonly={readonly}
                            key={`form-pessoa`}
                            isPessoaFisica={isPessoaFisica()}
                            isPessoaJuridica={!isPessoaFisica()}
                            dados={resolveDados()}
                            financeira_id={configfuracoesFinanceira?.id || null}
                            onChange={(_dados, _tipoPessoa) => {
                                const _state = CollectionUtil.merge({}, dados)
                                if (_tipoPessoa.isPessoaFisica) {
                                    _state.pessoa_fisica = _dados
                                } else if (_tipoPessoa.isPessoaJuridica) {
                                    _state.pessoa_juridica = _dados
                                }
                                onChange(_state)
                            }}
                            dadosCliente={dadosCliente}
                            isValid={(_valid) => {
                                if (isValid) {
                                    isValid(_valid)
                                }
                            }}
                        />
                    </div>
                </div>
            </TabContent>

            <TabContent name={TAB_DADOS_CONJUGE} label={<>
                <Avatar variant="rounded" color={theme.palette.primary.main}>
                    <FaRing />
                </Avatar> Dados Cônjuge{tabError["dados_pessoais.conjuge"] ? <div className="error-alert"><FaExclamationCircle color={theme.palette.error.main} /></div> : ""}</>}
                visible={(dados as any)?.pessoa_fisica?.estado_civil === ESTADO_CIVIL_CASADO_ID && resolveConfiguracoesFinanceiro().permite_conjuge && isPessoaFisica()}>
                {(dados as any)?.pessoa_fisica?.estado_civil === ESTADO_CIVIL_CASADO_ID && <div className="row">
                    {ArrayUtil.contains(errorsList,"pessoa_fisica.conjuge#") && <>{renderAlert()}</>}
                    <div className="X12">
                        <FormPessoa
                            key={`form-pessoa-conjuge`}
                            readonly={readonly}
                            dados={(dados as any)?.pessoa_fisica?.conjuge}
                            isConjuge
                            isPessoaFisica
                            isAvalista={false}
                            financeira_id={configfuracoesFinanceira?.id || null}
                            onChange={(_dados, _tipoPessoa) => {
                                const _state = { ...dados as any }
                                if (dados) {
                                    if (_tipoPessoa.isPessoaFisica && _tipoPessoa.isConjuge) {
                                        _state.pessoa_fisica.conjuge = _dados
                                    }

                                    if (_tipoPessoa.isAvalista) {
                                        // TODO
                                    }
                                    onChange(_state)
                                }
                            }}
                            // dadosCliente={dadosCliente}
                            isValid={(_valid) => {
                                if (isValid) {
                                    isValid(_valid)
                                }
                            }}
                        />
                    </div>
                </div>}
            </TabContent>

            <TabContent name={TAB_DADOS_PROFISSIONAIS} label={<>
                <Avatar variant="rounded" color={theme.palette.primary.main}>
                    <FaBriefcase />
                </Avatar>
                Dados Profissionais{tabError["dados_pessoais.profissionais"] ? <div className="error-alert"><FaExclamationCircle color={theme.palette.error.main} /></div> : ""}
            </>} visible={isPessoaFisica()}>
                <div className="row">
                    {ArrayUtil.contains(errorsList,"pessoa_fisica.ocupacao#") && <>{renderAlert()}</>}
                    <div className="X12">
                        <FormProfissao
                            key={`form-profissao-fisica`}
                            errorsList={errorsList}
                            readonly={readonly}
                            dadosProfissao={(dados as any)?.pessoa_fisica?.ocupacao || null}
                            dataNascimento={(dados as any)?.pessoa_fisica?.data_nascimento || null}
                            isPessoaFisica
                            financeira_id={configfuracoesFinanceira?.id || 0}
                            onChange={(_dados) => {
                                const _state = { ...dados as any }
                                _state.pessoa_fisica.ocupacao = _dados
                                onChange(_state)
                            }}
                        />
                    </div>
                </div>
            </TabContent>

            <TabContent name={TAB_DADOS_BANCARIOS} label={<>
                <Avatar variant="rounded" color={theme.palette.primary.main}>
                    <AiFillBank />
                </Avatar>
                Dados Bancários{tabError["dados_bancarios"] ? <div className="error-alert"><FaExclamationCircle color={theme.palette.error.main} /></div> : ""}</>}
                visible={!!configfuracoesFinanceira?.banco_id}>
                <div className="row">
                    <div className="X12">
                        <FormDadosBancarios
                            readonly={readonly}
                            dadosBancarios={(dados as any)?.dados_bancarios || null}
                            errorsList={errorsList}
                            onChange={(_dados) => {
                                const _state = { ...dados as any }
                                _state.dados_bancarios = _dados
                                onChange(_state)
                            }}
                            isValid={(_valid) => {
                                if (isValid) {
                                    isValid(_valid)
                                }
                            }}
                            bancoId={configfuracoesFinanceira?.banco_id}
                        />

                    </div>
                </div>
            </TabContent>

            <TabContent name={TAB_DADOS_AVALISTAS} label={<>
                <Avatar variant="rounded" color={theme.palette.primary.main}>
                    <FaUsers />
                </Avatar>
                Dados Avalistas{tabError["avalistas"] ? <div className="error-alert"><FaExclamationCircle color={theme.palette.error.main} /></div> : ""}
            </>} visible={resolveConfiguracoesFinanceiro().permite_avalistas}>
                <div className="row">
                    <div className="X12">
                        <FormAvalistas
                            quantidade_avalistas={(!isPessoaFisica() ? 1 : 0)}
                            avalistas={(dados as any).avalistas}
                            errorsList={errorsList}
                            financeiraId={configfuracoesFinanceira?.id}
                            onChange={(_avalistas) => {
                                const _state = CollectionUtil.merge({}, dados)
                                _state.avalistas = _avalistas
                                onChange(_state)
                            }}
                        />
                    </div>
                </div>
            </TabContent>
        </TabControl>
    </div>
}

export { Financiamento }